import React, { useState, useEffect, useRef } from 'react';
import { useData, DOCTOR_INFO } from '../context/DataContext';
import { useLoader } from '../context/LoaderContext';
import useApiPatients from '../hooks/useApiPatients';
import useApiBills from '../hooks/useApiBills';
import { createPatientBill, updatePatientBill, getPatientDropdown } from '../services/api';

// ── Searchable Patient Dropdown ──────────────────────────────
const SearchablePatientSelect = ({ value, onChange, patientList, required }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? patientList.filter(p =>
        p.name?.toLowerCase().includes(query.toLowerCase()) ||
        (p.mrNo || `MR-${p.id}`)?.toLowerCase().includes(query.toLowerCase())
      )
    : patientList;

  const selected = patientList.find(p => String(p.id) === String(value));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {selected ? `${selected.name} (${selected.mrNo || 'MR-' + selected.id})` : '-- Select Patient --'}
        </span>
        <span className="text-gray-400 text-xs flex-shrink-0">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[9999] w-full bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <input
              autoFocus
              type="text"
              placeholder="🔍 Search name or MR No..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 260 }}>
            <div
              className="px-4 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
            >-- Select Patient --</div>
            {filtered.length === 0 && (
              <div className="px-4 py-4 text-sm text-gray-400 text-center">No patients found</div>
            )}
            {filtered.map(p => (
              <div
                key={p.id}
                onClick={() => { onChange(String(p.id)); setOpen(false); setQuery(''); }}
                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between gap-2
                  ${String(p.id) === String(value) ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'}`}
              >
                <span className="truncate">{p.name}</span>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{p.mrNo || `MR-${p.id}`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Bills Management ──────────────────────────────────────────
const BillsManagement = () => {
  const { patients, bills, addBill, updateBill, addTransaction } = useData();
  const { showLoader, hideLoader } = useLoader();
  const { apiPatients } = useApiPatients();
  const { apiBills, loading: billsLoading, error: billsError, refetch: refetchBills } = useApiBills();

  const predefinedItems = [
    { id: 1,  name: 'Operation (C/Sec)',                  apiField: 'operation_CSec' },
    { id: 2,  name: 'Operation (TAH)',                    apiField: 'operation_TAH' },
    { id: 3,  name: 'Operation (Vag Hyst)',               apiField: 'operation_VagHyst' },
    { id: 4,  name: 'Operation (Myomectomy)',             apiField: 'operation_Myomectomy' },
    { id: 5,  name: 'Operation (Ap Repair & Laprotomy)',  apiField: 'operation_APRepair_Laparotomy' },
    { id: 6,  name: 'Operation (NVD/IUD)',                apiField: 'operation_NVD_IUD' },
    { id: 7,  name: 'Operation Fee',                      apiField: 'operationFee' },
    { id: 8,  name: 'Anesthesia Fee',                     apiField: 'anesthesiaFee' },
    { id: 9,  name: 'O.T / Labor Room Charges',           apiField: 'oT_LaborRoomCharges' },
    { id: 10, name: 'N.V.D / I.U.D Charges',             apiField: 'nvD_IUD_Charges' },
    { id: 11, name: 'D & C Charges',                      apiField: 'dC_Charges' },
    { id: 12, name: 'Room Rent @ Rs: 5000/- per day',     apiField: 'roomRent' },
    { id: 13, name: 'Child Specialist Fee',               apiField: 'childSpecialistFee' },
    { id: 14, name: 'Medicine Bill',                      apiField: 'medicineBill' },
    { id: 15, name: 'Admission in N.I.C.U',               apiField: 'admission_NICU' },
    { id: 16, name: 'Daycare in N.I.C.U',                 apiField: 'daycare_NICU' },
    { id: 17, name: 'Lab Test Charges',                   apiField: 'labTestCharges' },
  ];

  useEffect(() => {
    if (billsLoading) showLoader('Loading bills...');
    else hideLoader();
  }, [billsLoading]);

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [viewBill, setViewBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [manualItems, setManualItems] = useState([]);
  const [newManualItem, setNewManualItem] = useState({ name: '', amount: '' });
  const [advanceForm, setAdvanceForm] = useState({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' });
  const [submittingBill, setSubmittingBill] = useState(false);
  const [billSubmitMsg, setBillSubmitMsg] = useState('');
  const [editBillForm, setEditBillForm] = useState(null);
  const [editBillItems, setEditBillItems] = useState(predefinedItems.map(item => ({ ...item, amount: '', selected: false })));
  const [editManualItems, setEditManualItems] = useState([]);
  const [editNewManualItem, setEditNewManualItem] = useState({ name: '', amount: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editSubmitMsg, setEditSubmitMsg] = useState('');
  const [dropdownPatients, setDropdownPatients] = useState([]);

  const refreshDropdown = () => {
    getPatientDropdown()
      .then(data => { if (Array.isArray(data) && data.length > 0) setDropdownPatients(data); })
      .catch(() => {});
  };

  useEffect(() => { refreshDropdown(); }, []);

  const patientList = dropdownPatients.length > 0
    ? dropdownPatients
    : (apiPatients.length > 0 ? apiPatients : patients);

  const [form, setForm] = useState({
    patientId: '', room: '', operation: '',
    items: predefinedItems.map(item => ({ ...item, amount: '', selected: false })),
    advance: '', discount: '', returnAmount: ''
  });

 const displayBills = apiBills.length > 0
    ? apiBills.map(b => {
      const patient = patientList.find(p => String(p.id) === String(advanceForm.patientId));
        const extractedName = b.patientMRNo 
          ? b.patientMRNo.replace(/^MR-\d+\s*/i, '').trim() 
          : '';
       const items = predefinedItems
  .filter(pi => b[pi.apiField] > 0)
  .map(pi => ({ description: pi.name, amount: b[pi.apiField] }));

// Add customAmount as a line item if present
if (b.customAmount > 0) {
  items.push({ description: 'Other Charges', amount: b.customAmount });
}

return {
  id: b.id, _apiId: b.id, _apiRaw: b,
  date: b.billDate ? new Date(b.billDate).toLocaleDateString() : '-',
  mrNo: b.patientMRNo ? b.patientMRNo.match(/MR-\d+/i)?.[0] || b.patientMRNo : `MR-${b.patientId}`,
  patientName: patient ? patient.name : (extractedName || `Patient #${b.patientId}`),
  room: b.roomNo,
  items: items.length > 0 ? items : [{ description: 'Bill', amount: b.totalAmount }],
  totalAmount: b.totalAmount || 0, paidAmount: b.paidAmount || 0,
  discount: b.discount || 0, remainingAmount: b.remainingAmount || 0,
  status: (!b.status || b.status === 'string') ? (b.paidAmount > 0 && b.totalAmount === 0 ? 'advance' : b.remainingAmount > 0 ? 'unpaid' : 'paid') : b.status,
  isAdvance: false,
};
      })
    : bills;

  const filteredBills = displayBills.filter(b =>
    b.patientName?.toLowerCase().includes(search.toLowerCase()) ||
    b.mrNo?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    const patient = patientList.find(p => 
  String(p.id) === String(b.patientId) || 
  p.mrNo === b.patientMRNo
);
    if (!patient) { alert('Please select a patient'); return; }
    if (!advanceForm.amount || Number(advanceForm.amount) <= 0) { alert('Please enter a valid amount'); return; }
    showLoader('Recording advance payment...');
    const apiPayload = {
      id: 0, patientId: Number(advanceForm.patientId),
      patientMRNo: patient.mrNo || `MR-${patient.id}`, roomNo: '',
      billDate: new Date().toISOString(),
      operation_CSec: 0, operation_TAH: 0, operation_VagHyst: 0,
      operation_Myomectomy: 0, operation_APRepair_Laparotomy: 0, operation_NVD_IUD: 0,
      operationFee: 0, anesthesiaFee: 0, oT_LaborRoomCharges: 0,
      nvD_IUD_Charges: 0, dC_Charges: 0, roomRent: 0,
      childSpecialistFee: 0, medicineBill: 0, admission_NICU: 0,
      daycare_NICU: 0, labTestCharges: 0,
      totalAmount: 0, discount: 0,
      paidAmount: Number(advanceForm.amount), remainingAmount: 0,
      status: 'advance', createdOn: new Date().toISOString(),
    };
    try {
      await createPatientBill(apiPayload);
      if (addTransaction) addTransaction({
        type: 'income', category: 'Advance Payment',
        description: `Advance from ${patient.name} (${patient.mrNo || 'MR-' + patient.id}) - ${advanceForm.paymentMethod}${advanceForm.notes ? ' - ' + advanceForm.notes : ''}`,
        amount: Number(advanceForm.amount)
      });
      alert(`✅ Advance payment of Rs. ${Number(advanceForm.amount).toLocaleString()} recorded for ${patient.name}`);
      refetchBills(); refreshDropdown();
    } catch (err) {
      alert(`⚠️ API failed: ${err.message}.`);
    }
    hideLoader();
    setAdvanceForm({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' });
    setShowAdvanceForm(false);
  };

  const handleOpenEditBill = (bill) => {
    const raw = bill._apiRaw || {};
    const prefilledItems = predefinedItems.map(item => {
      const val = raw[item.apiField] ?? 0;
      return { ...item, amount: val > 0 ? String(val) : '', selected: val > 0 };
    });
    const customAmt = raw.customAmount || 0;
    const prefilledManual = customAmt > 0
      ? [{ id: Date.now(), name: 'Other Charges', amount: String(customAmt) }]
      : [];
    setEditBillItems(prefilledItems);
    setEditManualItems(prefilledManual);
    setEditBillForm(bill);
    setEditSubmitMsg('');
    setViewBill(null);
    setShowForm(false);        // ← CLOSE create form
    setShowAdvanceForm(false); // ← CLOSE advance form
  };

  const calculateEditTotals = () => {
    return editBillItems.reduce((sum, item) => sum + Number(item.amount || 0), 0) +
           editManualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  };

  const handleEditItemAmountChange = (id, amount) => {
    setEditBillItems(prev => prev.map(item =>
      item.id === id ? { ...item, amount, selected: amount !== '' && Number(amount) > 0 } : item
    ));
  };

  const addEditManualItem = () => {
    if (editNewManualItem.name && editNewManualItem.amount) {
      setEditManualItems(prev => [...prev, { ...editNewManualItem, id: Date.now() }]);
      setEditNewManualItem({ name: '', amount: '' });
    }
  };

  const handleEditBillSubmit = async (e) => {
    e.preventDefault();
    if (!editBillForm) return;
    const newTotal = calculateEditTotals();
    const existingPaid = Number(editBillForm.paidAmount || 0);
    const discount = Number(editBillForm.discount || 0);
    const newRemaining = newTotal - existingPaid - discount;
    setEditSubmitting(true);
    showLoader('Updating bill...');
    const raw = editBillForm._apiRaw || {};
   const updatePayload = {
  id: editBillForm._apiId,
  patientId: raw.patientId,
  patientMRNo: raw.patientMRNo || editBillForm.mrNo,
  roomNo: raw.roomNo || editBillForm.room || '',
  billDate: raw.billDate || new Date().toISOString(),
  totalAmount: newTotal,
  discount: discount,
  paidAmount: existingPaid,
  remainingAmount: newRemaining < 0 ? 0 : newRemaining,
  customAmount: editManualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
  status: newRemaining <= 0 ? 'Paid' : existingPaid > 0 ? 'Partial' : 'Unpaid',
  createdOn: raw.createdOn || new Date().toISOString(),
  // all predefined fields defaulted to 0 — overwritten below
  operation_CSec: 0, operation_TAH: 0, operation_VagHyst: 0,
  operation_Myomectomy: 0, operation_APRepair_Laparotomy: 0, operation_NVD_IUD: 0,
  operationFee: 0, anesthesiaFee: 0, oT_LaborRoomCharges: 0,
  nvD_IUD_Charges: 0, dC_Charges: 0, roomRent: 0,
  childSpecialistFee: 0, medicineBill: 0, admission_NICU: 0,
  daycare_NICU: 0, labTestCharges: 0,
};
    editBillItems.forEach(item => {
      updatePayload[item.apiField] = item.selected && Number(item.amount) > 0 ? Number(item.amount) : 0;
    });
    try {
      await updatePatientBill(updatePayload);
      setEditSubmitMsg(`✅ Bill updated! Total: Rs. ${newTotal.toLocaleString()} | Paid: Rs. ${existingPaid.toLocaleString()} | Due: Rs. ${Math.max(0, newRemaining).toLocaleString()}`);
      refetchBills();
      setTimeout(() => { setEditBillForm(null); setEditSubmitMsg(''); }, 2500);
    } catch (err) {
      setEditSubmitMsg(`⚠️ Failed: ${err.message}`);
    }
    setEditSubmitting(false);
    hideLoader();
  };

  const handleItemAmountChange = (id, amount) => {
    setForm({ ...form, items: form.items.map(item =>
      item.id === id ? { ...item, amount, selected: amount !== '' && Number(amount) > 0 } : item
    )});
  };

  const addManualItem = () => {
    if (newManualItem.name && newManualItem.amount) {
      setManualItems([...manualItems, { ...newManualItem, id: Date.now() }]);
      setNewManualItem({ name: '', amount: '' });
    }
  };

  const removeManualItem = (id) => setManualItems(manualItems.filter(item => item.id !== id));

  const calculateTotals = () => {
    const totalAmount = form.items.reduce((sum, item) => sum + Number(item.amount || 0), 0) +
                        manualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const advance = Number(form.advance || 0);
    const discount = Number(form.discount || 0);
    const returnAmt = Number(form.returnAmount || 0);
    return { totalAmount, advance, discount, returnAmt, balance: totalAmount - advance - discount - returnAmt };
  };

  const resetForm = () => {
    setForm({
      patientId: '', room: '', operation: '',
      items: predefinedItems.map(item => ({ ...item, amount: '', selected: false })),
      advance: '', discount: '', returnAmount: ''
    });
    setManualItems([]);
    setShowForm(false);
    setBillSubmitMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const patient = patientList.find(p => String(p.id) === String(form.patientId));
    const selectedItems = form.items.filter(item => item.selected && Number(item.amount) > 0);
    const allItems = [
      ...selectedItems.map(item => ({ description: item.name, amount: item.amount })),
      ...manualItems.map(item => ({ description: item.name, amount: item.amount }))
    ];
    const totals = calculateTotals();
    if (allItems.length === 0) { alert('Please add at least one item with amount'); return; }
    setSubmittingBill(true); setBillSubmitMsg('');
    showLoader('Creating bill...');
    const apiPayload = {
      id: 0, patientId: Number(form.patientId),
      patientMRNo: patient?.mrNo || `MR-${form.patientId}`,
      roomNo: form.room || '', billDate: new Date().toISOString(),
      operation_CSec: 0, operation_TAH: 0, operation_VagHyst: 0,
      operation_Myomectomy: 0, operation_APRepair_Laparotomy: 0, operation_NVD_IUD: 0,
      operationFee: 0, anesthesiaFee: 0, oT_LaborRoomCharges: 0,
      nvD_IUD_Charges: 0, dC_Charges: 0, roomRent: 0,
      childSpecialistFee: 0, medicineBill: 0, admission_NICU: 0,
      daycare_NICU: 0, labTestCharges: 0,
   totalAmount: totals.totalAmount, discount: totals.discount,
paidAmount: totals.advance, remainingAmount: totals.balance,
customAmount: manualItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
status: totals.balance <= 0 ? 'paid' : 'unpaid',
createdOn: new Date().toISOString(),
    };
    form.items.forEach(item => {
      if (item.selected && Number(item.amount) > 0) apiPayload[item.apiField] = Number(item.amount);
    });
    try {
      const result = await createPatientBill(apiPayload);
      setBillSubmitMsg(`✅ Bill saved! Bill ID: ${result.billId}`);
      if (addBill) addBill({ patientId: form.patientId, patientName: patient?.name, mrNo: patient?.mrNo || `MR-${form.patientId}`, room: form.room, items: allItems, totalAmount: totals.totalAmount, advance: totals.advance, discount: totals.discount, returnAmount: totals.returnAmt, balance: totals.balance, paidAmount: totals.advance, status: totals.balance <= 0 ? 'paid' : 'unpaid' });
      refetchBills(); refreshDropdown();
      setTimeout(resetForm, 2000);
    } catch (err) {
      setBillSubmitMsg(`⚠️ API failed: ${err.message}. Saved locally.`);
      if (addBill) addBill({ patientId: form.patientId, patientName: patient?.name, mrNo: patient?.mrNo || `MR-${form.patientId}`, room: form.room, items: allItems, totalAmount: totals.totalAmount, advance: totals.advance, discount: totals.discount, returnAmount: totals.returnAmt, balance: totals.balance, paidAmount: totals.advance, status: totals.balance <= 0 ? 'paid' : 'unpaid' });
      refreshDropdown();
      setTimeout(resetForm, 2000);
    }
    setSubmittingBill(false); hideLoader();
  };

  const handlePayment = async () => {
    if (!viewBill || !paymentAmount) return;
    const amount = Number(paymentAmount);
    const newPaid = Number(viewBill.paidAmount || 0) + amount;
    const newRemaining = Number(viewBill.totalAmount) - Number(viewBill.discount || 0) - newPaid;
    const newStatus = newRemaining <= 0 ? 'paid' : 'partial';
    setPaymentUpdating(true);
    showLoader('Recording payment...');
    if (viewBill._apiId) {
      const raw = viewBill._apiRaw;
      const matchedPatient = patientList.find(p => p.mrNo === viewBill.mrNo || String(p.id) === String(raw?.patientId));
      const correctPatientId = matchedPatient?.id || raw?.patientId || 0;
      try {
        await updatePatientBill({ ...raw, id: viewBill._apiId, patientId: correctPatientId, paidAmount: newPaid, remainingAmount: newRemaining < 0 ? 0 : newRemaining, status: newStatus });
        refetchBills();
      } catch (err) { alert(`API update failed: ${err.message}`); }
    }
    if (updateBill) updateBill(viewBill.id, { paidAmount: newPaid, status: newStatus });
    if (addTransaction) addTransaction({ type: 'income', category: 'Patient Bill', description: `Payment from ${viewBill.patientName}`, amount });
    setPaymentUpdating(false); hideLoader();
    setPaymentAmount(''); setViewBill(null);
  };

  const printBill = (bill) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Bill - ${bill.mrNo}</title>
      <style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; } .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 10px; margin-bottom: 15px; } .clinic-name { font-size: 18px; font-weight: bold; color: #1e40af; } .address { font-size: 10px; color: #666; } .invoice-info { display: flex; justify-content: space-between; margin-bottom: 15px; padding: 10px; background: #f5f5f5; } .title { font-size: 16px; font-weight: bold; text-align: center; margin: 10px 0; } table { width: 100%; border-collapse: collapse; margin: 15px 0; } th, td { border: 1px solid #ddd; padding: 8px; text-align: left; } th { background: #1e40af; color: white; } .amount { text-align: right; } .total-row { font-weight: bold; background: #e3f2fd; } .summary { background: #f5f5f5; padding: 15px; margin-top: 15px; } .summary-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #ddd; } .summary-row:last-child { border-bottom: none; } .balance { font-size: 16px; font-weight: bold; color: ${Number(bill.remainingAmount || 0) > 0 ? '#dc2626' : '#16a34a'}; } .signature { display: flex; justify-content: space-between; margin-top: 50px; } .sig-box { text-align: center; } .sig-line { border-top: 1px solid #000; width: 120px; margin-bottom: 5px; } @media print { body { padding: 10px; } }</style></head>
      <body>
        <div class="header"><div class="clinic-name">${DOCTOR_INFO.clinicName}</div><div class="address">${DOCTOR_INFO.address}</div><div class="address">Phone: ${DOCTOR_INFO.phone}</div></div>
        <div class="title">INVOICE / BILL</div>
        <div class="invoice-info"><div><p><strong>Patient:</strong> ${bill.patientName}</p><p><strong>MR No:</strong> ${bill.mrNo}</p>${bill.room ? `<p><strong>Room:</strong> ${bill.room}</p>` : ''}</div><div style="text-align:right;"><p><strong>Date:</strong> ${bill.date}</p><p><strong>Status:</strong> ${bill.status?.toUpperCase()}</p></div></div>
        <table><thead><tr><th style="width:50px;">S.No</th><th>Particulars</th><th style="width:120px;" class="amount">Amount (Rs.)</th></tr></thead>
        <tbody>${bill.items?.map((item, i) => `<tr><td>${i + 1}</td><td>${item.description}</td><td class="amount">${Number(item.amount).toLocaleString()}</td></tr>`).join('')}
        <tr class="total-row"><td colspan="2"><strong>Total Amount</strong></td><td class="amount"><strong>Rs. ${Number(bill.totalAmount).toLocaleString()}</strong></td></tr></tbody></table>
        <div class="summary"><div class="summary-row"><span>Total Amount:</span><span>Rs. ${Number(bill.totalAmount).toLocaleString()}</span></div>${bill.discount ? `<div class="summary-row"><span>Discount:</span><span>Rs. ${Number(bill.discount).toLocaleString()}</span></div>` : ''}<div class="summary-row"><span>Amount Paid:</span><span style="color:#16a34a;">Rs. ${Number(bill.paidAmount || 0).toLocaleString()}</span></div><div class="summary-row"><span><strong>Balance Due:</strong></span><span class="balance">Rs. ${Number(bill.remainingAmount || 0).toLocaleString()}</span></div></div>
        <div class="signature"><div class="sig-box"><div class="sig-line"></div><div>Patient/Attendant</div></div><div class="sig-box"><div class="sig-line"></div><div>Cashier</div></div></div>
        <script>window.onload = function() { window.print(); }</script>
      </body></html>`);
    printWindow.document.close();
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">Bills Management</h2>
          {billsLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {billsError && <span className="text-xs text-red-500">⚠️ API Error</span>}
          {!billsLoading && !billsError && apiBills.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600">✅ Live API ({apiBills.filter(b => b.totalAmount > 0).length} bills)</span>
              <button onClick={refetchBills} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-field max-w-xs" />
          <button onClick={() => setShowAdvanceForm(true)} className="btn-success text-sm">💰 Add Advance</button>
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Create Bill</button>
        </div>
      </div>

      {/* Advance Form */}
      {showAdvanceForm && (
        <div className="card p-6 border-2 border-green-400">
          <h3 className="font-bold text-green-800 mb-4">💰 Record Advance Payment</h3>
          <form onSubmit={handleAdvanceSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Select Patient *</label><SearchablePatientSelect value={advanceForm.patientId} onChange={(val) => setAdvanceForm({ ...advanceForm, patientId: val })} patientList={patientList} required /></div>
              <div><label className="block text-sm font-medium mb-1">Advance Amount (Rs.) *</label><input type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="input-field" min="1" required /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Payment Method</label><select value={advanceForm.paymentMethod} onChange={(e) => setAdvanceForm({ ...advanceForm, paymentMethod: e.target.value })} className="input-field"><option value="Cash">Cash</option><option value="Bank Transfer">Bank Transfer</option><option value="JazzCash/Easypaisa">JazzCash/Easypaisa</option><option value="Cheque">Cheque</option></select></div>
              <div><label className="block text-sm font-medium mb-1">Notes (Optional)</label><input type="text" value={advanceForm.notes} onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })} className="input-field" /></div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-success text-sm">💰 Record Advance</button>
              <button type="button" onClick={() => { setShowAdvanceForm(false); setAdvanceForm({ patientId: '', amount: '', paymentMethod: 'Cash', notes: '' }); }} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Create Bill Form */}
      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4 text-center text-lg border-b pb-2">Create New Bill - {DOCTOR_INFO.clinicName}</h3>
          {billSubmitMsg && <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${billSubmitMsg.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{billSubmitMsg}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Select Patient *</label><SearchablePatientSelect value={form.patientId} onChange={(val) => setForm({ ...form, patientId: val })} patientList={patientList} required />{dropdownPatients.length > 0 && <p className="text-xs text-green-600 mt-1">✅ {dropdownPatients.length} patients loaded</p>}</div>
              <div><label className="block text-sm font-medium mb-1">Room #</label><input type="text" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Date</label><input type="text" value={new Date().toLocaleDateString()} className="input-field bg-gray-50" readOnly /></div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-blue-50"><tr><th className="px-3 py-2 text-left border-b w-12">S.No</th><th className="px-3 py-2 text-left border-b">Particulars</th><th className="px-3 py-2 text-right border-b w-32">Rupees</th></tr></thead>
                <tbody>
                  {form.items.map((item, index) => (
                    <tr key={item.id} className={item.selected ? 'bg-green-50' : ''}>
                      <td className="px-3 py-2 border-b text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 border-b">{item.name}</td>
                      <td className="px-3 py-2 border-b"><input type="number" placeholder="0" value={item.amount} onChange={(e) => handleItemAmountChange(item.id, e.target.value)} className="input-field text-right w-full" /></td>
                    </tr>
                  ))}
                  {manualItems.map((item, index) => (
                    <tr key={item.id} className="bg-yellow-50">
                      <td className="px-3 py-2 border-b text-gray-500">{form.items.length + index + 1}</td>
                      <td className="px-3 py-2 border-b">{item.name} <span className="text-xs text-yellow-600">(Custom)</span></td>
                      <td className="px-3 py-2 border-b flex items-center gap-1"><span className="text-right flex-1">Rs. {Number(item.amount).toLocaleString()}</span><button type="button" onClick={() => removeManualItem(item.id)} className="text-red-500 hover:text-red-700">×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">➕ Add Custom Item</h4>
              <div className="flex gap-2">
  <input type="text" placeholder="Item name..." value={newManualItem.name} onChange={(e) => setNewManualItem({ ...newManualItem, name: e.target.value })} className="input-field" style={{flex: 3}} />
  <input type="number" placeholder="Amount" value={newManualItem.amount} onChange={(e) => setNewManualItem({ ...newManualItem, amount: e.target.value })} className="input-field" style={{flex: 1, minWidth: '80px', maxWidth: '120px'}} />
  <button type="button" onClick={addManualItem} className="btn-success text-sm whitespace-nowrap">Add</button>
</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center py-2 border-b"><span className="font-bold">Total Amount</span><span className="font-bold text-lg">Rs. {totals.totalAmount.toLocaleString()}</span></div>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Advance</label><input type="number" placeholder="0" value={form.advance} onChange={(e) => setForm({ ...form, advance: e.target.value })} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Discount</label><input type="number" placeholder="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Return</label><input type="number" placeholder="0" value={form.returnAmount} onChange={(e) => setForm({ ...form, returnAmount: e.target.value })} className="input-field" /></div>
              </div>
              <div className="flex justify-between items-center py-2 border-t mt-2"><span className="font-bold text-lg">Balance Due</span><span className={`font-bold text-xl ${totals.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {totals.balance.toLocaleString()}</span></div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={submittingBill} className={`btn-primary text-sm ${submittingBill ? 'opacity-50 cursor-not-allowed' : ''}`}>{submittingBill ? '⏳ Saving...' : 'Create Bill'}</button>
              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Bills Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Date</th><th>MR No</th><th>Patient</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredBills.map(bill => (
                <tr key={`${bill._apiId || ''}-${bill.id}`}>
                  <td>{bill.date}</td>
                  <td className="font-mono text-sm">{bill.mrNo}</td>
                  <td className="font-medium">{bill.patientName}</td>
                  <td>Rs. {Number(bill.totalAmount).toLocaleString()}</td>
                  <td className="text-green-600 font-medium">Rs. {Number(bill.paidAmount || 0).toLocaleString()}</td>
                  <td className={Number(bill.remainingAmount || 0) > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>Rs. {Number(bill.remainingAmount || 0).toLocaleString()}</td>
                  <td><span className={`status-badge status-${bill.status}`}>{bill.status}</span></td>
<td><div className="flex gap-1"><button onClick={() => setViewBill(bill)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">View</button><button onClick={() => handleOpenEditBill(bill)} className="text-xs px-2 py-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200">✏️ Edit</button><button onClick={() => printBill(bill)} className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200">🖨️</button></div></td>
                </tr>
              ))}
              {filteredBills.length === 0 && !billsLoading && (<tr><td colSpan="8" className="text-center py-8 text-gray-500">No bills found</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Bill Modal */}
      {viewBill && (
        <div className="modal-overlay" onClick={() => { setViewBill(null); setPaymentAmount(''); }}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Bill Details - {viewBill.mrNo}</h3>
              <div className="flex gap-2"><button onClick={() => printBill(viewBill)} className="btn-success text-sm">🖨️ Print</button><button onClick={() => { setViewBill(null); setPaymentAmount(''); }} className="text-gray-500 text-2xl leading-none">×</button></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg text-sm space-y-1"><p><strong>Patient:</strong> {viewBill.patientName}</p><p><strong>MR No:</strong> {viewBill.mrNo}</p>{viewBill.room && <p><strong>Room:</strong> {viewBill.room}</p>}<p><strong>Date:</strong> {viewBill.date}</p></div>
              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1"><p><strong>Status:</strong> <span className={`px-2 py-0.5 rounded text-xs font-medium ${viewBill.status === 'paid' ? 'bg-green-100 text-green-700' : viewBill.status === 'advance' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{viewBill.status?.toUpperCase()}</span></p>{viewBill._apiId && <p><strong>Bill ID:</strong> #{viewBill._apiId}</p>}</div>
            </div>
            <div className="border rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-blue-50"><tr><th className="px-3 py-2 text-left border-b w-12">S.No</th><th className="px-3 py-2 text-left border-b">Particulars</th><th className="px-3 py-2 text-right border-b w-36">Amount (Rs.)</th></tr></thead>
                <tbody>{viewBill.items?.map((item, i) => (<tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}><td className="px-3 py-2 border-b text-gray-500">{i + 1}</td><td className="px-3 py-2 border-b">{item.description}</td><td className="px-3 py-2 border-b text-right font-medium">{Number(item.amount) > 0 ? `Rs. ${Number(item.amount).toLocaleString()}` : '-'}</td></tr>))}</tbody>
              </table>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-white rounded-lg p-3 border"><p className="text-xs text-gray-500 mb-1">Total Billed</p><p className="font-bold text-gray-900">Rs. {Number(viewBill.totalAmount || 0).toLocaleString()}</p></div>
                {viewBill.discount > 0 && <div className="bg-white rounded-lg p-3 border"><p className="text-xs text-gray-500 mb-1">Discount</p><p className="font-bold text-orange-600">Rs. {Number(viewBill.discount || 0).toLocaleString()}</p></div>}
                <div className="bg-white rounded-lg p-3 border"><p className="text-xs text-gray-500 mb-1">Amount Paid</p><p className="font-bold text-green-600">Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p></div>
                <div className="bg-white rounded-lg p-3 border"><p className="text-xs text-gray-500 mb-1">Balance Due</p><p className={`font-bold ${Number(viewBill.remainingAmount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {Number(viewBill.remainingAmount || 0).toLocaleString()}</p></div>
              </div>
            </div>
            {viewBill.status !== 'paid' && (
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50 mb-4">
                <h4 className="font-bold text-green-800 mb-3">💰 Record Payment</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Payment Amount (Rs.) *</label><input type="number" placeholder="Enter amount..." value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="input-field" min="1" /></div>
                  <div className="flex items-end"><button onClick={handlePayment} disabled={paymentUpdating || !paymentAmount} className={`btn-success w-full ${paymentUpdating || !paymentAmount ? 'opacity-50 cursor-not-allowed' : ''}`}>{paymentUpdating ? '⏳ Recording...' : '✅ Record Payment'}</button></div>
                </div>
                {Number(viewBill.remainingAmount || 0) > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 self-center">Quick:</span>
                    {[500, 1000, 2000, 5000].map(amt => (<button key={amt} onClick={() => setPaymentAmount(String(amt))} className="text-xs px-3 py-1 bg-white border border-green-300 text-green-700 rounded-full hover:bg-green-100">Rs. {amt.toLocaleString()}</button>))}
                    <button onClick={() => setPaymentAmount(String(Math.max(0, Number(viewBill.remainingAmount || 0))))} className="text-xs px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700">Full Due: Rs. {Number(viewBill.remainingAmount || 0).toLocaleString()}</button>
                  </div>
                )}
              </div>
            )}
            {viewBill.status === 'paid' && (<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center"><p className="text-green-700 font-bold text-lg">✅ Bill Fully Paid</p><p className="text-green-600 text-sm">Total paid: Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p></div>)}
            {viewBill.status === 'advance' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-center">
                <p className="text-blue-700 font-bold mb-2">💰 Advance Paid: Rs. {Number(viewBill.paidAmount || 0).toLocaleString()}</p>
                <p className="text-blue-600 text-sm mb-3">Patient has paid advance. Click below to fill the complete bill details.</p>
                <button onClick={() => handleOpenEditBill(viewBill)} className="btn-primary">📋 Fill Complete Bill Details</button>
              </div>
            )}
            <button onClick={() => { setViewBill(null); setPaymentAmount(''); }} className="btn-secondary w-full text-sm">Close</button>
          </div>
        </div>
      )}

      {/* Edit Bill Modal */}
      {editBillForm && (
        <div className="modal-overlay" onClick={() => setEditBillForm(null)}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="text-xl font-bold">📋 Fill Bill Details - {editBillForm.mrNo}</h3><button onClick={() => setEditBillForm(null)} className="text-gray-500 text-2xl">×</button></div>
{Number(editBillForm.paidAmount || 0) > 0 && (
  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-3">
    <span className="text-2xl">💰</span>
    <div>
      <p className="font-bold text-green-800">Already Paid: Rs. {Number(editBillForm.paidAmount || 0).toLocaleString()}</p>
      <p className="text-green-600 text-sm">This amount will be deducted from the total bill automatically.</p>
    </div>
  </div>
)}            {editSubmitMsg && <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${editSubmitMsg.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{editSubmitMsg}</div>}
            <form onSubmit={handleEditBillSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Patient</label><input type="text" value={editBillForm.patientName} className="input-field bg-gray-50" readOnly /></div>
                <div><label className="block text-sm font-medium mb-1">MR No</label><input type="text" value={editBillForm.mrNo} className="input-field bg-gray-50" readOnly /></div>
                <div><label className="block text-sm font-medium mb-1">Date</label><input type="text" value={new Date().toLocaleDateString()} className="input-field bg-gray-50" readOnly /></div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50"><tr><th className="px-3 py-2 text-left border-b w-12">S.No</th><th className="px-3 py-2 text-left border-b">Particulars</th><th className="px-3 py-2 text-right border-b w-32">Rupees</th></tr></thead>
                  <tbody>
                    {editBillItems.map((item, index) => (<tr key={item.id} className={item.selected ? 'bg-green-50' : ''}><td className="px-3 py-2 border-b text-gray-500">{index + 1}</td><td className="px-3 py-2 border-b">{item.name}</td><td className="px-3 py-2 border-b"><input type="number" placeholder="0" value={item.amount} onChange={(e) => handleEditItemAmountChange(item.id, e.target.value)} className="input-field text-right w-full" /></td></tr>))}
                    {editManualItems.map((item, index) => (<tr key={item.id} className="bg-yellow-50"><td className="px-3 py-2 border-b text-gray-500">{editBillItems.length + index + 1}</td><td className="px-3 py-2 border-b">{item.name} <span className="text-xs text-yellow-600">(Custom)</span></td><td className="px-3 py-2 border-b flex items-center gap-1"><span className="flex-1 text-right">Rs. {Number(item.amount).toLocaleString()}</span><button type="button" onClick={() => setEditManualItems(prev => prev.filter(i => i.id !== item.id))} className="text-red-500">×</button></td></tr>))}
                  </tbody>
                </table>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">➕ Add Custom Item</h4>
                <div className="flex gap-2"><input type="text" placeholder="Item name..." value={editNewManualItem.name} onChange={(e) => setEditNewManualItem({ ...editNewManualItem, name: e.target.value })} className="input-field flex-1" /><input type="number" placeholder="Amount" value={editNewManualItem.amount} onChange={(e) => setEditNewManualItem({ ...editNewManualItem, amount: e.target.value })} className="input-field w-32" /><button type="button" onClick={addEditManualItem} className="btn-success text-sm">Add</button></div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center py-2 border-b"><span className="font-bold">Total Bill Amount</span><span className="font-bold text-lg">Rs. {calculateEditTotals().toLocaleString()}</span></div>
                <div className="flex justify-between items-center py-1"><span className="text-gray-600">Advance Paid</span><span className="text-green-600 font-medium">- Rs. {Number(editBillForm.paidAmount || 0).toLocaleString()}</span></div>
                <div className="flex justify-between items-center py-2 border-t mt-2"><span className="font-bold text-lg">Balance Due</span><span className={`font-bold text-xl ${calculateEditTotals() - Number(editBillForm.paidAmount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>Rs. {Math.max(0, calculateEditTotals() - Number(editBillForm.paidAmount || 0) - Number(editBillForm.discount || 0)).toLocaleString()}</span></div>
              </div>
              <div className="flex gap-2"><button type="submit" disabled={editSubmitting} className={`btn-primary flex-1 ${editSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>{editSubmitting ? '⏳ Saving...' : '💾 Save Bill Details'}</button><button type="button" onClick={() => setEditBillForm(null)} className="btn-secondary">Cancel</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Finance Panel ─────────────────────────────────────────────
const FinancePanel = ({ user, onLogout }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-gray-900">Finance Panel</h1>
              <p className="text-xs text-gray-500">Bills & Payment Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">💰 {user.name}</span>
            <button onClick={onLogout} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <BillsManagement />
      </div>
    </div>
  );
};

export default FinancePanel;